import { useMemo } from "react";

export type AdItem = {
  type: "ad";
  key: string;
  slotId: string;
};

export type ListItemWithAd<T> = { type: "item"; data: T; key: string } | AdItem;

export type UseAdsInListOptions = {
  /** 広告を挿入する間隔（アイテム数） */
  interval?: number;
  /** 最初の広告を表示するまでのアイテム数 */
  startAfter?: number;
  /** 広告の最大数 */
  maxAds?: number;
  /** 広告ユニットID（複数指定可能、ローテーション） */
  slotIds: string[];
  /** 広告を有効にするかどうか */
  enabled?: boolean;
};

/**
 * リストに広告を挿入するユーティリティフック
 *
 * @example
 * ```tsx
 * const itemsWithAds = useAdsInList(expenses, {
 *   interval: 6,
 *   startAfter: 5,
 *   slotIds: ["1234567890"],
 * });
 *
 * return itemsWithAds.map((item) =>
 *   item.type === "ad" ? (
 *     <NativeAd key={item.key} slotId={item.slotId} />
 *   ) : (
 *     <ExpenseCard key={item.key} expense={item.data} />
 *   )
 * );
 * ```
 */
export function useAdsInList<T>(items: T[], options: UseAdsInListOptions): ListItemWithAd<T>[] {
  const { interval = 6, startAfter = 5, maxAds = 5, slotIds, enabled = true } = options;

  return useMemo(() => {
    if (!enabled || slotIds.length === 0) {
      return items.map((data, index) => ({
        type: "item" as const,
        data,
        key: `item-${index}`,
      }));
    }

    const result: ListItemWithAd<T>[] = [];
    let adCount = 0;

    items.forEach((data, index) => {
      result.push({
        type: "item",
        data,
        key: `item-${index}`,
      });

      // 広告挿入条件をチェック
      const itemPosition = index + 1; // 1-indexed
      const shouldInsertAd =
        adCount < maxAds &&
        itemPosition >= startAfter &&
        (itemPosition - startAfter) % interval === 0;

      if (shouldInsertAd) {
        const slotId = slotIds[adCount % slotIds.length];
        result.push({
          type: "ad",
          key: `ad-${adCount}`,
          slotId,
        });
        adCount++;
      }
    });

    return result;
  }, [items, interval, startAfter, maxAds, slotIds, enabled]);
}
